import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Edit, Building2, X, Plus, Search } from 'lucide-react';
import api from '../services/api';

function MultasPendientes() {
  const [multas, setMultas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [multaSeleccionada, setMultaSeleccionada] = useState(null);
  const [creandoEmpresa, setCreandoEmpresa] = useState(false);
  const [nuevaEmpresa, setNuevaEmpresa] = useState({ nombre: '', cif: '' });
  const [error, setError] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    cargarMultasPendientes();
  }, []);

  const cargarMultasPendientes = async () => {
    try {
      const response = await api.get('/admin/multas/pendientes');
      setMultas(response.data.data);
    } catch (error) {
      console.error('Error cargando multas:', error);
      // El interceptor de api.js maneja el 401 automáticamente
    } finally {
      setLoading(false);
    }
  };

  const handleIdentificar = (multa) => {
    if (!multa.empresa_id) {
      setMultaSeleccionada(multa);
      setNuevaEmpresa({
        nombre: multa.nombre_titular || '',
        cif: multa.nif_titular || ''
      });
      setShowModal(true);
    } else {
      navigate('/admin/multas/' + multa.id + '/editar');
    }
  };

  const crearEmpresaYContinuar = async () => {
    if (!nuevaEmpresa.nombre || !nuevaEmpresa.cif) {
      setError('Nombre y CIF son obligatorios');
      return;
    }

    setCreandoEmpresa(true);
    setError('');

    try {
      const response = await api.post('/admin/empresas', nuevaEmpresa);

      if (response.data.success) {
        const empresaId = response.data.data.id;

        await api.put('/admin/multas/' + multaSeleccionada.id + '/asignar-empresa', { empresa_id: empresaId });

        setShowModal(false);
        navigate('/admin/multas/' + multaSeleccionada.id + '/editar');
      }
    } catch (error) {
      console.error('Error creando empresa:', error);
      setError(error.response?.data?.error || 'Error al crear empresa');
    } finally {
      setCreandoEmpresa(false);
    }
  };

  const continuarSinEmpresa = () => {
    setShowModal(false);
    navigate('/admin/multas/' + multaSeleccionada.id + '/editar');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Multas Pendientes</h1>
            <p className="text-sm text-gray-600">Requieren identificacion manual</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Buscador */}
        {multas.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por matrícula, expediente, organismo, NIF..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="w-full pl-12 pr-12 py-3 text-lg border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
              {busqueda && (
                <button
                  onClick={() => setBusqueda('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-4 h-4 text-gray-400" />
                </button>
              )}
            </div>
          </div>
        )}

        {multas.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl">
            <AlertCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No hay multas pendientes
            </h3>
            <p className="text-gray-600">
              Todas las multas han sido identificadas automaticamente
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {multas.filter(m => {
              if (!busqueda) return true;
              const search = busqueda.toLowerCase().trim();
              return (
                m.numero_expediente?.toLowerCase().includes(search) ||
                m.matricula?.toLowerCase().includes(search) ||
                m.organismo_emisor?.toLowerCase().includes(search) ||
                m.nif_titular?.toLowerCase().includes(search) ||
                m.empresa_nombre?.toLowerCase().includes(search) ||
                m.metadata?.numero_notificacion?.toLowerCase().includes(search)
              );
            }).map((multa) => (
              <div key={multa.id} className={'bg-white p-6 rounded-xl shadow-sm ' + (!multa.empresa_id ? 'border-l-4 border-orange-400' : '')}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="bg-orange-100 p-3 rounded-lg">
                        <AlertCircle className="w-6 h-6 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">
                          <span className="text-xs font-mono text-gray-400 mr-2">#{multa.id}</span>
                          {multa.numero_expediente}
                        </h3>
                        {/* Mostrar número de notificación si es diferente del expediente */}
                        {multa.metadata?.numero_notificacion &&
                         multa.metadata.numero_notificacion !== multa.numero_expediente && (
                          <p className="text-xs text-slate-400">
                            Notif: {multa.metadata.numero_notificacion}
                          </p>
                        )}
                        {multa.empresa_nombre ? (
                          <p className="text-sm text-gray-600">{multa.empresa_nombre}</p>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                            <Building2 className="w-3 h-3" />
                            Sin empresa asignada
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="grid md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Matricula</p>
                        <p className="text-lg font-bold text-gray-900">{multa.matricula}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Fecha Infraccion</p>
                        <p className="text-gray-900">
                          {new Date(multa.fecha_infraccion).toLocaleDateString('es-ES')}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Organismo</p>
                        <p className="text-gray-900">{multa.organismo_emisor || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">NIF Titular</p>
                        <p className="text-gray-900">{multa.nif_titular || 'N/A'}</p>
                      </div>
                    </div>

                    {multa.observaciones && (
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-600">{multa.observaciones}</p>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => handleIdentificar(multa)}
                    className="ml-4 flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                    Identificar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Empresa no registrada</h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <Building2 className="w-5 h-5 text-orange-600 mt-0.5" />
                <div>
                  <p className="text-sm text-orange-800 font-medium">Esta multa no tiene empresa asignada</p>
                  <p className="text-sm text-orange-700 mt-1">
                    Titular: {multaSeleccionada?.nombre_titular || 'N/A'}<br/>
                    NIF: {multaSeleccionada?.nif_titular || 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg mb-4 text-sm">
                {error}
              </div>
            )}

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de empresa</label>
                <input
                  type="text"
                  value={nuevaEmpresa.nombre}
                  onChange={(e) => setNuevaEmpresa({...nuevaEmpresa, nombre: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nombre de la empresa"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CIF/NIF</label>
                <input
                  type="text"
                  value={nuevaEmpresa.cif}
                  onChange={(e) => setNuevaEmpresa({...nuevaEmpresa, cif: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="CIF de la empresa"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={crearEmpresaYContinuar}
                disabled={creandoEmpresa}
                className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Plus className="w-4 h-4" />
                {creandoEmpresa ? 'Creando...' : 'Crear empresa'}
              </button>
              <button
                onClick={continuarSinEmpresa}
                className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200"
              >
                Continuar sin empresa
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MultasPendientes;
